/* eslint-disable camelcase */
import axios from '@/services/axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// Routes that remain accessible while a v2 store is mid-onboarding.
// Only the wizard host (`apps-v2`) is reachable; everything else -- reports,
// management, even staff management -- is hidden from the sidebar and
// blocked by the router guard until onboarding completes.
const ALLOWED_ROUTE_NAMES = new Set([
  'apps-v2',
])

export const useV2OnboardingLockStore = defineStore('v2OnboardingLock', () => {
  const isLocked = ref(false)
  const lockedStoreId = ref('')
  const hasFetched = ref(false)

  const allowedRouteNames = computed(() => ALLOWED_ROUTE_NAMES)

  function isRouteAllowed(routeName) {
    if (!isLocked.value) {
      return true
    }

    return ALLOWED_ROUTE_NAMES.has(String(routeName || ''))
  }

  /**
   * Apply lock state derived from the v2 onboarding status payload.
   * Called by the v2 dashboard page after it fetches /api/v2/onboarding/status,
   * so we don't refetch from the router guard in the common case.
   */
  function applyStatus({ storeId, onboardingRequired, canAccessSetup }) {
    isLocked.value = Boolean(onboardingRequired) && Boolean(canAccessSetup)
    lockedStoreId.value = String(storeId || '')
    hasFetched.value = true
  }

  /**
   * Fetch onboarding status directly. Used by the router guard on the very
   * first navigation (before the v2 dashboard has had a chance to mount), and
   * by anything else that needs an authoritative read.
   */
  async function refresh() {
    try {
      const response = await axios.get('/api/v2/onboarding/status')
      const payload = response.data || {}

      applyStatus({
        storeId: payload.store_id,
        onboardingRequired: payload.onboarding_required,
        canAccessSetup: payload.can_access_setup,
      })
    } catch {
      // On failure (e.g. no v2 access at all), do not lock -- fail open so
      // the rest of the app remains usable.
      isLocked.value = false
      lockedStoreId.value = ''
      hasFetched.value = true
    }
  }

  function clear() {
    isLocked.value = false
    lockedStoreId.value = ''
    hasFetched.value = false
  }

  return {
    isLocked,
    lockedStoreId,
    hasFetched,
    allowedRouteNames,
    isRouteAllowed,
    applyStatus,
    refresh,
    clear,
  }
})
